import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismLoadingBar,
  PrismModal,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t, tKey } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { Illustration } from 'frontend-utils';
import KpiSectionCustomIllustration from 'lumen/assets/images/pms-mapping/kpi-section-custom.svg?raw';
import KpiSectionDefaultIllustration from 'lumen/assets/images/pms-mapping/kpi-section-default.svg?raw';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import {
  KPI_DISPLAY_CUSTOM_MAPPING,
  KPI_DISPLAY_DEFAULT_MAPPING,
} from 'lumen/utils/constants/pms-mapping-constants';

interface SettingsMappingModalsKpiSectionModalSignature {
  Args: {
    isEndOfMapping: unknown;
    onCloseModal: unknown;
    refreshRoute: unknown;
  };
}

export default class SettingsMappingModalsKpiSectionModal extends Component<SettingsMappingModalsKpiSectionModalSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // @ts-expect-error: Incorrect type
  @tracked revenueInsightKpiDisplay = this.hotel.revenueInsightKpiDisplay;

  saveKpiSectionTask = task({ drop: true }, async () => {
    this.usageAnalytics.track('Settings Changed', {
      eventTrigger: 'kpi-display-settings',
      settingsName: 'kpi-display',
    });

    // @ts-expect-error: Incorrect type
    this.hotel.revenueInsightKpiDisplay = this.revenueInsightKpiDisplay;
    // @ts-expect-error: Incorrect type
    await this.hotel.save();

    // @ts-expect-error: Incorrect type
    // Refresh route if we're on Revenue Insight, because the data from API will be different.
    if (this.router.currentRouteName.indexOf('revenue-insight') !== -1) {
      // @ts-expect-error: Incorrect type
      this.args.refreshRoute();
    }

    this.toast.success(this.intl.t('general.changes_saved.body'), {
      title: this.intl.t('general.changes_saved'),
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });
    // @ts-expect-error: Incorrect type
    this.args.onCloseModal();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translationKeys() {
    if (this.args.isEndOfMapping) {
      return {
        title: tKey(
          'settings.pms_mapping.modal_kpi_display_saved_changes.subheading',
        ),
        text: tKey('settings.pms_mapping.modal_kpi_display_saved_changes.body'),
        defaultOption: tKey(
          'settings.pms_mapping.modal_kpi_display_saved_changes.no_later',
        ),
        mappingOption: tKey(
          'settings.pms_mapping.modal_kpi_display_saved_changes.use_new_mapping',
        ),
        cancel: tKey(
          'settings.pms_mapping.modal_kpi_display.keep_current_display',
        ),
        confirm: tKey(
          'settings.pms_mapping.modal_kpi_display.change_kpi_display',
        ),
      };
    }

    return {
      text: tKey('settings.pms_mapping.modal_kpi_display.body'),
      defaultOption: tKey('general.default'),
      mappingOption: tKey(
        'settings.pms_mapping.modal_kpi_display.my_hierarchy',
      ),
      cancel: tKey(
        'settings.pms_mapping.modal_kpi_display.keep_current_display',
      ),
      confirm: tKey(
        'settings.pms_mapping.modal_kpi_display.change_kpi_display',
      ),
    };
  }

  @action
  // @ts-expect-error: Incorrect type
  setKpiDisplay(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.revenueInsightKpiDisplay = event.target.value;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <PrismModal
      {{! @glint-expect-error: Incorrect type }}
      @onClose={{@onCloseModal}}
      @title={{t "settings.pms_mapping.modal_kpi_display.heading"}}
    >
      <div class="modal--small modal--padded settings-modal__content">
        {{#if this.saveKpiSectionTask.isRunning}}
          <PrismLoadingBar />
        {{/if}}

        {{#if this.translationKeys.title}}
          <p class="settings-modal__title text-300--regular">
            {{t this.translationKeys.title}}
          </p>
        {{/if}}

        <p class="settings-modal__text paragraph-300--regular">
          {{t this.translationKeys.text}}
        </p>

        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="kpi-display-radio-group"
          @onChange={{this.setKpiDisplay}}
          @value={{this.revenueInsightKpiDisplay}}
          as |RadioGroup|
        >
          <RadioGroup.input @value={{KPI_DISPLAY_DEFAULT_MAPPING}}>
            {{t this.translationKeys.defaultOption}}
          </RadioGroup.input>
          <RadioGroup.input @value={{KPI_DISPLAY_CUSTOM_MAPPING}}>
            {{t this.translationKeys.mappingOption}}
          </RadioGroup.input>
        </PrismRadioGroup>

        <div class="mt-500">
          {{#if (eq this.revenueInsightKpiDisplay KPI_DISPLAY_CUSTOM_MAPPING)}}
            <Illustration
              @html={{KpiSectionCustomIllustration}}
              aria-label="KPI section custom"
              class="settings-modal__illustration"
            />
          {{else}}
            <Illustration
              @html={{KpiSectionDefaultIllustration}}
              aria-label="KPI section default"
              class="settings-modal__illustration"
            />
          {{/if}}
        </div>
      </div>

      <div class="settings-modal__footer">
        <div class="flex w-full items-center justify-between">
          <GeneralNavigationIntercomHelpLink
            @articleLink="en/articles/5592427-choosing-your-kpi-display-in-revenue-insight"
            @iconName="book"
            @isStandaloneLink={{true}}
            @linkText={{t "general.help_center"}}
            @type="text-and-icon"
          />

          <GeneralFormFieldsCancelAndConfirm
            {{! @glint-expect-error: Incorrect type }}
            @cancel={{@onCloseModal}}
            @cancelText={{t "general.cancel"}}
            @confirm={{perform this.saveKpiSectionTask}}
            @confirmText={{t "general.apply"}}
            @isConfirmDisabled={{eq
              this.revenueInsightKpiDisplay
              this.hotel.revenueInsightKpiDisplay
            }}
          />
        </div>
      </div>
    </PrismModal>
  </template>
}
